<div class="mermaid-generator-container">
  <!-- LIST VIEW -->
  @if (viewMode() === 'LIST') {
    <div class="list-view">
      <div class="header list-header">
        <div class="header-titles">
          <h2>{{ 'MermaidGenerator.Title' | translate }}</h2>
          <p>{{ 'MermaidGenerator.ManagementSubtitle' | translate }}</p>
        </div>
        <div class="header-actions">
          <button mat-icon-button (click)="close.emit()">
            <mat-icon>close</mat-icon>
          </button>
        </div>
      </div>
      <div class="content list-content">
        <div class="section-block">
          <div class="create-cards">
            @if (!cannotUseAI()) {
              <div class="create-card ai-card" (click)="openAiSetup()">
                <div class="card-icon-wrapper ai-icon-wrapper">
                  <mat-icon>account_tree</mat-icon>
                </div>
                <div class="card-info">
                  <h4>{{ 'MermaidGenerator.GenerateWithAI' | translate }}</h4>
                  <p>{{ 'MermaidGenerator.GenerateAIDesc' | translate }}</p>
                </div>
              </div>
            }
          </div>
        </div>
        <mat-divider class="mb-3"></mat-divider>
        <div class="section-block">
          <div class="draft-list">
            @for (draft of visibleDrafts(); track draft._id) {
              <div class="course-list-item draft-item" (click)="openDraft(draft)">
                <div class="item-icon-wrapper">
                  <mat-icon>account_tree</mat-icon>
                </div>
                <div class="item-content-row">
                  <h4 class="item-title">{{ draft.title || ('MermaidGenerator.DraftTitle' | translate) }}</h4>
                  <div class="item-subtitle">
                    <span>{{ draft.updatedAt | date: 'dd/MM/yyyy HH:mm' }}</span>
                    @if (draft.status === 'generating') {
                      <span class="text-warning">• Generating</span>
                    }
                  </div>
                </div>
                <div class="item-actions-row">
                  @if (insertingDraftIds().has(draft._id!)) {
                    <mat-spinner diameter="18"></mat-spinner>
                  } @else {
                    <button
                      #insertBtn
                      mat-icon-button
                      class="action-btn"
                      (click)="confirmInsertDraft($event, draft, insertBtn)"
                      [disabled]="insertingDraftIds().has(draft._id!)"
                      matTooltip="{{ 'ContentRefiner.InsertButton' | translate }}"
                    >
                      <mat-icon>arrow_insert</mat-icon>
                    </button>
                  }
                  <button #deleteBtn mat-icon-button class="delete-btn" (click)="deleteDraft(draft._id!, $event, deleteBtn)">
                    <mat-icon>delete_outline</mat-icon>
                  </button>
                </div>
              </div>
            }
            @if (visibleDrafts().length === 0) {
              <div class="empty-state">
                <p>{{ 'MermaidGenerator.NoDrafts' | translate }}</p>
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  }

  <!-- AI SETUP MODE -->
  @if (viewMode() === 'SETUP') {
    <div class="header">
      <div class="flex items-center gap-2">
        <button mat-icon-button (click)="goBackToList()">
          <mat-icon>arrow_back</mat-icon>
        </button>
        <h2 class="m-0">{{ 'MermaidGenerator.AiGeneratorTitle' | translate }}</h2>
      </div>
      <button mat-icon-button (click)="close.emit()">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  }

  @if (viewMode() === 'SETUP') {
    <div class="content ai-setup-content" (dragover)="onDragOver($event)" (dragenter)="onDragEnter($event)" (dragleave)="onDragLeave($event)" (drop)="onDrop($event)">
      @if (isDraggingOver()) {
        <div class="drop-overlay">
          <div class="drop-content">
            <mat-icon>cloud_upload</mat-icon>
            <p>{{ 'FileUploader.DropFileHere' | translate }}</p>
          </div>
        </div>
      }
      <form [formGroup]="aiSetupForm">
        <!-- Subject -->
        <label class="form-label">{{ 'MermaidGenerator.AiSubjectLabel' | translate }} *</label>
        <mat-form-field appearance="fill" class="setup-field" subscriptSizing="dynamic">
          <textarea matInput formControlName="subject" [placeholder]="'MermaidGenerator.AiSubjectPlaceholder' | translate" cdkTextareaAutosize cdkAutosizeMinRows="4" cdkAutosizeMaxRows="8"></textarea>
        </mat-form-field>

        <!-- Diagram Type and AI Model Inline Row -->
        <div class="grid grid-cols-2 gap-4 mt-3 mb-3">
          <div class="flex flex-col min-w-0">
            <label class="form-label mt-0">{{ 'MermaidGenerator.DiagramTypeLabel' | translate }}</label>
            <ui-select class="w-full" [compact]="true" formControlName="diagramType" [options]="diagramTypeOptions" [placeholder]="'GenerateContent.Mermaid.TypeAuto' | translate" />
          </div>
          <div class="flex flex-col min-w-0">
            <label class="form-label mt-0">{{ 'MermaidGenerator.AiModelLabel' | translate }}</label>
            <ui-select class="w-full" [compact]="true" formControlName="model" [options]="modelOptions" [placeholder]="'GenerateContent.Mermaid.ModelFast' | translate" />
          </div>
        </div>

        <!-- Customization -->
        <label class="form-label">{{ 'MermaidGenerator.AiCustomizationLabel' | translate }}</label>
        <mat-form-field appearance="fill" class="setup-field" subscriptSizing="dynamic">
          <textarea
            matInput
            cdkTextareaAutosize
            cdkAutosizeMinRows="4"
            cdkAutosizeMaxRows="8"
            formControlName="customization"
            [placeholder]="'MermaidGenerator.CustomizationHint' | translate"
          ></textarea>
        </mat-form-field>

        <!-- Media Context Indicator -->
        @if (attachedFiles() && attachedFiles().length > 0) {
          <div class="attached-files-container mt-3">
            @for (file of attachedFiles(); track file.id) {
              <div class="media-context-card mb-2 flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div class="flex items-center gap-3">
                  <div class="icon-wrapper flex items-center justify-center w-10 h-10 rounded-lg bg-indigo-50 text-indigo-500">
                    @if (file.uploading) {
                      <mat-spinner diameter="20"></mat-spinner>
                    } @else {
                      @if (file.type === 'video') {
                        <mat-icon class="media-icon">videocam</mat-icon>
                      } @else if (file.type === 'image') {
                        <mat-icon class="media-icon">image</mat-icon>
                      } @else if (file.type === 'audio') {
                        <mat-icon class="media-icon">mic</mat-icon>
                      } @else {
                        <mat-icon class="media-icon">description</mat-icon>
                      }
                    }
                  </div>
                  <div class="info-wrapper flex flex-col">
                    <h4 [matTooltip]="file.name" class="m-0 text-sm font-medium max-w-[200px] truncate">{{ file.name }}</h4>
                    @if (file.uploading) {
                      <p class="m-0 text-xs text-gray-500">{{ 'FileUploader.Uploading' | translate }}...</p>
                    } @else {
                      <p class="m-0 text-xs text-gray-500">{{ 'ContentGenerator.FileAttached' | translate }}</p>
                    }
                  </div>
                </div>
                <button type="button" mat-icon-button class="remove-btn" (click)="removeAttachment(file.id)">
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            }
          </div>
        }

        <!-- External Context Indicator -->
        @if (attachedExternalContexts() && attachedExternalContexts().length > 0) {
          <div class="attached-files-container mt-3">
            @for (ext of attachedExternalContexts(); track ext.id) {
              <div class="media-context-card mb-2 flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div class="flex items-center gap-3">
                  <div class="icon-wrapper flex items-center justify-center w-10 h-10 rounded-lg bg-indigo-50 text-indigo-500">
                    <mat-icon class="media-icon">{{ ext.type === 'lecture' ? 'school' : ext.type === 'note' ? 'sticky_note_2' : 'article' }}</mat-icon>
                  </div>
                  <div class="info-wrapper flex flex-col">
                    <h4 [matTooltip]="ext.title" class="m-0 text-sm font-medium max-w-[200px] truncate">{{ ext.title }}</h4>
                    <p class="m-0 text-xs text-gray-500">{{ 'ContentGenerator.FileAttached' | translate }}</p>
                  </div>
                </div>
                <button type="button" mat-icon-button class="remove-btn" (click)="removeExternalContext(ext.id)">
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            }
          </div>
        }

        <div class="file-actions mt-3 flex gap-2 flex-wrap">
          <button type="button" mat-stroked-button (click)="fileInput.click()">
            <mat-icon>attach_file</mat-icon>
            {{ 'ContentGenerator.AttachFile' | translate }}
          </button>
          <button type="button" mat-stroked-button (click)="toggleContentSearcher()">
            <mat-icon>docs_add_on</mat-icon>
            {{ 'Searcher.AttachLibraryContent' | translate }}
          </button>
          <input type="file" #fileInput hidden multiple (change)="onFileSelected($event)" />
        </div>
      </form>
    </div>
  }

  <!-- AI LOADING MODE -->
  @if (viewMode() === 'LOADING') {
    <div class="content ai-loading-content">
      <div class="loading-wrapper">
        <div class="pulsing-icon">
          <mat-icon>account_tree</mat-icon>
        </div>
        <h3>{{ 'MermaidGenerator.Generating' | translate }}</h3>
        <p>{{ 'MermaidGenerator.GeneratingDesc' | translate }}</p>
        <div class="spinner-dots">
          <mat-spinner diameter="24"></mat-spinner>
        </div>
      </div>
    </div>
  }

  <!-- SETUP FOOTER -->
  @if (viewMode() === 'SETUP') {
    <div class="footer">
      <button mat-flat-button color="primary" class="insert-btn" (click)="generateMermaidDiagram()" [disabled]="aiSetupForm.invalid || !allFilesDoneUploading()">
        {{ 'Global.Generate' | translate }}
      </button>
      <div class="restart-link-wrapper">
        <a class="restart-link" (click)="cancelSetup()">{{ 'Global.Cancel' | translate }}</a>
      </div>
    </div>
  }
</div>
